import { defaultBusiness as NAP } from "@/lib/site-config";
import { createFileRoute, Link } from "@tanstack/react-router";
import { AlertTriangle, Phone } from "lucide-react";
import { seoHead } from "@/lib/seo";
import { ResourceShareBlock } from "@/components/ResourceShareBlock";
import { FAQAccordion, faqSchema } from "@/components/FAQAccordion";
import { CTABlock } from "@/components/CTABlock";
import { useBusinessConfig } from "@/lib/use-business-config";

interface Light {
  name: string;
  meaning: string;
  drive: string;
  next: string;
  linkTo: string;
  linkLabel: string;
}

const LIGHTS: Light[] = [
  {
    name: "Engine management light (amber)",
    meaning: "The car has detected an emissions or engine fault and stored a code.",
    drive: "Usually yes if the car drives normally, but get it diagnosed soon — it will fail MOT if left on.",
    next: "Book a diagnostic scan to read the fault code.",
    linkTo: "/diagnostics",
    linkLabel: "Engine Diagnostics",
  },
  {
    name: "Flashing engine management light",
    meaning: "Serious misfire — fuel can be entering the catalytic converter.",
    drive: "No. Pull over safely and stop driving.",
    next: "Call a garage for advice and arrange recovery if needed.",
    linkTo: "/contact",
    linkLabel: "Call us",
  },
  {
    name: "ABS warning",
    meaning: "Anti-lock braking system has a fault — normal brakes still work, ABS may not.",
    drive: "With care. Avoid emergency stops where possible.",
    next: "Book a diagnostic — often a wheel speed sensor.",
    linkTo: "/diagnostics",
    linkLabel: "Engine Diagnostics",
  },
  {
    name: "Brake warning (red)",
    meaning: "Low brake fluid, brake pad wear warning, or handbrake on.",
    drive: "Check the handbrake is fully released. If light stays on, stop when safe.",
    next: "Brakes are safety-critical — call a garage immediately.",
    linkTo: "/mechanical-repairs",
    linkLabel: "Mechanical Repairs",
  },
  {
    name: "Oil pressure (red)",
    meaning: "Low oil pressure — engine can be damaged in minutes.",
    drive: "No. Stop as soon as safe and switch the engine off.",
    next: "Check oil level. If correct, do not restart — call for advice.",
    linkTo: "/contact",
    linkLabel: "Call us",
  },
  {
    name: "Battery / charging (red)",
    meaning: "The battery is not being charged — alternator or belt fault.",
    drive: "Limited — battery will eventually go flat. Switch off non-essentials.",
    next: "Get it checked the same day.",
    linkTo: "/mechanical-repairs",
    linkLabel: "Mechanical Repairs",
  },
  {
    name: "Airbag / SRS",
    meaning: "Airbag system fault — airbags may not deploy in a collision.",
    drive: "Yes, but safety system is compromised.",
    next: "Diagnostic scan — often a seat or buckle sensor.",
    linkTo: "/diagnostics",
    linkLabel: "Engine Diagnostics",
  },
  {
    name: "Tyre pressure (TPMS)",
    meaning: "One or more tyres below the expected pressure, or a sensor fault.",
    drive: "Check pressures at the nearest petrol station before continuing far.",
    next: "If pressures are correct, the sensor may need a reset or replacement.",
    linkTo: "/mot",
    linkLabel: "MOT Testing",
  },
  {
    name: "Coolant temperature (red)",
    meaning: "Engine is overheating — can cause major damage if ignored.",
    drive: "No. Pull over and let the engine cool before opening the bonnet.",
    next: "Do not top up a hot engine — call for advice.",
    linkTo: "/contact",
    linkLabel: "Call us",
  },
  {
    name: "DPF warning (diesel)",
    meaning: "Diesel particulate filter is blocked or not regenerating.",
    drive: "Often yes briefly — a longer drive may complete a regen, but don't ignore.",
    next: "If it doesn't clear, book a DPF check.",
    linkTo: "/diesel-dpf-egr-east-london",
    linkLabel: "Diesel DPF / EGR",
  },
  {
    name: "AdBlue / SCR (diesel)",
    meaning: "AdBlue level low, or SCR system fault.",
    drive: "Yes, but the car may eventually refuse to restart if ignored.",
    next: "Top up AdBlue first, then book a check if the light stays on.",
    linkTo: "/diesel-dpf-egr-east-london",
    linkLabel: "Diesel DPF / EGR",
  },
];

const FAQS = [
  {
    q: "Is it safe to drive with a warning light on?",
    a: "It depends on the colour and the system. Red warning lights generally mean stop when safe. Amber warning lights usually mean drive with care and get it checked soon. If you're unsure, call us before driving further.",
  },
  {
    q: "Can a warning light be reset without fixing the fault?",
    a: "Sometimes yes, but the underlying fault is still there and the light will return. It will also fail your MOT. Get it diagnosed properly.",
  },
  {
    q: "Do you do diagnostic scans?",
    a: `Yes — see our diagnostics page or call ${NAP.phone}. We tell you what the code means in plain English before quoting any work.`,
  },
];

export const Route = createFileRoute("/resources/dashboard-warning-light-guide")({
  head: () =>
    seoHead({
      path: "/resources/dashboard-warning-light-guide",
      title: "Dashboard Warning Light Guide | What to Do Next",
      description:
        "Free dashboard warning light guide: engine light, ABS, brake, oil, battery, airbag and tyre pressure warnings. Know when to stop and when to call a garage.",
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Free Resources", path: "/free-car-resources" },
        { name: "Warning Light Guide", path: "/resources/dashboard-warning-light-guide" },
      ],
      jsonLd: [faqSchema(FAQS) as Record<string, unknown>],
    }),
  component: WarningLightGuide,
});

function WarningLightGuide() {
  const biz = useBusinessConfig();
  return (
    <>
      <section className="bg-foreground text-background py-14 md:py-20 no-print">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-primary">Free resource</p>
          <h1 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight">
            Dashboard Warning Light Action Guide
          </h1>
          <p className="mt-4 text-base text-background/70 leading-relaxed max-w-2xl">
            A quick reference for common warning lights — what they may mean, whether you can keep driving,
            and what to do next.
          </p>
          <a
            href={biz.phoneHref}
            className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
          >
            <Phone className="h-4 w-4" /> Call {biz.phone}
          </a>
        </div>
      </section>

      <div className="print-only px-6 pt-6 text-foreground">
        <p className="text-xl font-extrabold">Woodseer Autos — Dashboard Warning Light Guide</p>
        <p className="text-sm">{NAP.phone} · woodseerautos.co.uk · 14 Copenhagen Place, London E14 7DF</p>
      </div>

      <section className="py-12 md:py-16">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-5 mb-8">
            <div className="flex items-start gap-3 text-foreground">
              <AlertTriangle className="h-5 w-5 text-primary shrink-0 mt-0.5" />
              <p className="text-sm leading-relaxed">
                <strong>Safety first.</strong> If a red warning light appears or the vehicle feels unsafe,
                stop when safe to do so and seek professional help. This guide is general advice — your
                vehicle handbook is the authoritative source.
              </p>
            </div>
          </div>

          <div className="space-y-4">
            {LIGHTS.map((l) => (
              <div key={l.name} className="rounded-xl border border-border bg-card p-5">
                <h2 className="text-base md:text-lg font-bold text-foreground">{l.name}</h2>
                <dl className="mt-3 grid sm:grid-cols-3 gap-4 text-sm">
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">What it may mean</dt>
                    <dd className="mt-1 text-foreground leading-relaxed">{l.meaning}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Can I keep driving?</dt>
                    <dd className="mt-1 text-foreground leading-relaxed">{l.drive}</dd>
                  </div>
                  <div>
                    <dt className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">What to do next</dt>
                    <dd className="mt-1 text-foreground leading-relaxed">{l.next}</dd>
                  </div>
                </dl>
                <div className="mt-3">
                  <Link to={l.linkTo} className="text-sm font-semibold text-primary hover:underline">
                    {l.linkLabel} →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FAQAccordion faqs={FAQS} title="Warning lights — FAQs" />
      <ResourceShareBlock path="/resources/dashboard-warning-light-guide" title="Dashboard Warning Light Action Guide" />
      <CTABlock headline="Light still on?" subtext="Diagnostic scan in plain English — call us first." />
    </>
  );
}
